import React, { useState, useEffect, useRef } from 'react';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';

import AreaTitle from '../../components/areaTitle/AreaTitle';
import { DataProviderInstance } from '../../api/DataProvider';
import { CreatePDF } from '../../utils/Utils';
import { parseMoney, cleanMoneyTyping } from '../../utils/moneyUtils';
import Toast from '../../components/support/Toast';
import BudgetCustomerForm from './components/BudgetCustomerForm';
import BudgetItems from './components/BudgetItems';
import BudgetExtraInfo from './components/BudgetExtraInfo';
import BudgetSummary from './components/BudgetSummary';
import BudgetPickerDialog from './components/BudgetPickerDialog';
import BudgetPreviewDialog from './components/BudgetPreviewDialog';
import { formatCurrency } from './components/budgetFormat';
import styles from './BudgetGeneratePage.module.css';

const initialBudgetData = {
  customer: { name: '', cpf: '', phone: '', email: '' },
  items: [],
  discount: 0,
  subtotal: 0,
  total: 0,
  validityDays: '',
  notes: '',
};

/** "dd/mm/aaaa" de hoje + N dias; vazio quando não há validade. */
const validUntilFromDays = (days) => {
  const count = parseInt(days, 10);
  if (!count || count <= 0) return '';
  const date = new Date();
  date.setDate(date.getDate() + count);
  return date.toLocaleDateString('pt-BR');
};

function BudgetGeneratePage() {
  const dataProvider = DataProviderInstance();
  const { CompanyConfig, GetPackages, UserData } = dataProvider;

  const budgetTemplateRef = useRef(null);

  // ── Estado principal ──────────────────────────────────────────────────────
  const [budgetData, setBudgetData] = useState(initialBudgetData);
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);

  // ── Dialogs ───────────────────────────────────────────────────────────────
  const [procedureDialogOpen, setProcedureDialogOpen] = useState(false);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // ── Nome do profissional ──────────────────────────────────────────────────
  const professionalName =
    UserData?.name || UserData?.Name || UserData?.profile?.name || '';

  // ── Carregar pacotes ──────────────────────────────────────────────────────
  useEffect(() => {
    if (GetPackages) {
      setLoadingPackages(true);
      GetPackages((result) => {
        // A API devolve { success, data }; só pacotes ativos entram no orçamento
        const list = Array.isArray(result) ? result : (Array.isArray(result?.data) ? result.data : []);
        setPackages(list.filter((pkg) => pkg.active));
        setLoadingPackages(false);
      });
    } else {
      setLoadingPackages(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Recalcular totais quando items ou discount mudam ──────────────────────
  useEffect(() => {
    const subtotal = budgetData.items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    const total = Math.max(0, subtotal - (parseFloat(budgetData.discount) || 0));
    setBudgetData((prev) => ({ ...prev, subtotal, total }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetData.items, budgetData.discount]);

  // ── Helpers de item ───────────────────────────────────────────────────────
  const addItem = (item) => {
    setBudgetData((prev) => ({
      ...prev,
      items: [...prev.items, { ...item, id: Date.now() }],
    }));
  };

  const removeItem = (id) => {
    setBudgetData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));
  };

  const updateItem = (id, field, value) => {
    setBudgetData((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };
        // Preço digitado como no Brasil ("1.200,99"): o texto fica no campo até sair dele
        if (field === 'unitPrice') {
          const text = cleanMoneyTyping(value);
          updated.unitPriceText = text;
          value = parseMoney(text) ?? 0;
          updated.unitPrice = value;
        }
        if (field === 'quantity' || field === 'unitPrice') {
          const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(item.quantity) || 0;
          const price = field === 'unitPrice' ? parseFloat(value) || 0 : parseFloat(item.unitPrice) || 0;
          updated.totalPrice = qty * price;
        }
        return updated;
      }),
    }));
  };

  const addProcedure = (procedure) => {
    addItem({
      description: procedure.Label,
      quantity: 1,
      unitPrice: parseFloat(procedure.Price) || 0,
      totalPrice: parseFloat(procedure.Price) || 0,
    });
  };

  const addPackage = (pkg) => {
    addItem({
      description: pkg.name,
      quantity: 1,
      unitPrice: parseFloat(pkg.price) || 0,
      totalPrice: parseFloat(pkg.price) || 0,
    });
  };

  const addManualItem = () => {
    addItem({ description: '', quantity: 1, unitPrice: 0, unitPriceText: '', totalPrice: 0 });
  };

  // ── Campos simples (interessado, validade, observações, desconto) ────────
  const handleCustomerChange = (field, value) => {
    setBudgetData((prev) => ({
      ...prev,
      customer: { ...prev.customer, [field]: value },
    }));
  };

  const handleFieldChange = (field, value) => {
    setBudgetData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDiscountChange = (value) => {
    setBudgetData((prev) => ({ ...prev, discount: parseFloat(value) || 0 }));
  };

  // ── Gerar PDF ─────────────────────────────────────────────────────────────
  const handleSavePDF = () => {
    if (budgetTemplateRef.current) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const datePart = `${yyyy}-${mm}-${dd}`;

      // CPF limpo (apenas dígitos), ou nome sanitizado, ou vazio
      const rawCpf = budgetData.customer.cpf?.replace(/\D/g, '') || '';
      const rawName = budgetData.customer.name?.trim() || '';
      const customerPart = rawCpf
        ? rawCpf
        : rawName
          ? rawName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
          : '';

      const fileName = customerPart
        ? `orcamento-${datePart}-${customerPart}.pdf`
        : `orcamento-${datePart}.pdf`;

      Promise.resolve(CreatePDF(budgetTemplateRef.current, fileName))
        .then(() => setToast({ open: true, message: 'PDF do orçamento salvo', severity: 'success' }))
        .catch(() => setToast({ open: true, message: 'Não foi possível salvar o PDF. Tente novamente.', severity: 'error' }));
      setPreviewOpen(false);
    }
  };

  // ── Procedimentos disponíveis (apenas ativos) ─────────────────────────────
  const procedures = (CompanyConfig?.Procedures || []).filter(p => p.Active == 1);

  // ── Data de emissão ───────────────────────────────────────────────────────
  const emissionDate = new Date().toLocaleDateString('pt-BR');
  const validUntil = validUntilFromDays(budgetData.validityDays);

  return (
    <div className="page-content">
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <AreaTitle
            Title="Orçamentos"
            Description="Monte o orçamento, confira a prévia e salve em PDF."
            Icon={<RequestQuoteOutlinedIcon />}
          />
          <span className={styles.dateChip}>
            <EventOutlinedIcon className={styles.dateChipIcon} />
            Emissão {emissionDate}
          </span>
        </div>

        <div className={styles.layout}>
          <div className={styles.main}>
            <BudgetCustomerForm customer={budgetData.customer} onChange={handleCustomerChange} />
            <BudgetItems
              items={budgetData.items}
              onAddProcedure={() => {
                setSelectedProcedure(null);
                setProcedureDialogOpen(true);
              }}
              onAddPackage={() => {
                setSelectedPackage(null);
                setPackageDialogOpen(true);
              }}
              onAddManual={addManualItem}
              onUpdate={updateItem}
              onRemove={removeItem}
            />
            <BudgetExtraInfo
              validityDays={budgetData.validityDays}
              validUntil={validUntil}
              notes={budgetData.notes}
              onChange={handleFieldChange}
            />
          </div>

          <div className={styles.aside}>
            <BudgetSummary
              itemCount={budgetData.items.length}
              subtotal={budgetData.subtotal}
              discount={budgetData.discount}
              total={budgetData.total}
              onDiscountChange={handleDiscountChange}
              onGenerate={() => setPreviewOpen(true)}
            />
          </div>
        </div>
      </div>

      <BudgetPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onSave={handleSavePDF}
        templateRef={budgetTemplateRef}
        companyData={CompanyConfig}
        budgetData={{ ...budgetData, validUntil }}
        date={emissionDate}
        professionalName={professionalName}
      />

      <BudgetPickerDialog
        open={procedureDialogOpen}
        onClose={() => setProcedureDialogOpen(false)}
        icon={<AssignmentOutlinedIcon />}
        title="Adicionar procedimento"
        subtitle="Entra com o valor cadastrado. Dá para ajustar depois."
        inputLabel="Procedimento"
        noOptionsText="Nenhum procedimento encontrado"
        options={procedures}
        getOptionLabel={(option) =>
          `${option.Label}${option.Price ? ` — ${formatCurrency(option.Price)}` : ''}`
        }
        value={selectedProcedure}
        onChange={setSelectedProcedure}
        onConfirm={() => {
          if (selectedProcedure) {
            addProcedure(selectedProcedure);
            setSelectedProcedure(null);
            setProcedureDialogOpen(false);
          }
        }}
      />

      <BudgetPickerDialog
        open={packageDialogOpen}
        onClose={() => setPackageDialogOpen(false)}
        icon={<Inventory2OutlinedIcon />}
        title="Adicionar pacote"
        subtitle="Entra com o valor do pacote. Dá para ajustar depois."
        inputLabel="Pacote"
        noOptionsText="Nenhum pacote encontrado"
        options={packages}
        getOptionLabel={(option) =>
          `${option.name}${option.price ? ` — ${formatCurrency(parseFloat(option.price))}` : ''}`
        }
        value={selectedPackage}
        onChange={setSelectedPackage}
        loading={loadingPackages}
        onConfirm={() => {
          if (selectedPackage) {
            addPackage(selectedPackage);
            setSelectedPackage(null);
            setPackageDialogOpen(false);
          }
        }}
      />
      <Toast
        open={toast.open}
        onClose={() => setToast({ ...toast, open: false })}
        message={toast.message}
        severity={toast.severity}
      />
    </div>
  );
}

export default BudgetGeneratePage;
